<template>
	<div class="SearchableRadioFilter">
		<Multiselect
			v-bind="multiselectProps"
			@updateValues="handleMultiselectUpdate"
		/>

		<div class="List">
			<RadioSelectItem
				v-for="entry in displayedItems"
				:key="entry.item.value"
				:item="entry.item"
				:subItems="entry.subItems"
				:name="groupName"
				:highlightSelectedText="highlightSelectedText"
				@selectionChange="(state, changedItem) => handleItemChange(changedItem || entry.item, state)"
			/>
			<button
				v-if="showMoreIndicator"
				type="button"
				class="ShowAll"
				:aria-expanded="showAll"
				:aria-label="showAll ? 'Show less' : `Show all (${items.length})`"
				@click="showHideAll"
			>
				{{ showAllText }}
			</button>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, watch, type PropType } from 'vue';
import Multiselect from '@components/Multiselect/Multiselect.vue';
import RadioSelectItem from '@components/RadioSelectItem/RadioSelectItem.vue';
import type { ISelectItem } from '@app/interfaces/dtos/ISelectItem';
import type { IMultiselectOption } from '@app/interfaces/dtos/IMultiselectOption';
import { SelectListItemStateType } from '@app/enums/SelectListItemStateType';
import { MultiselectMode } from '@app/enums/MultiselectMode';
import { ItemEntry } from '@app/interfaces/useMultiselect.interface';

const props = defineProps({
	items: {
		type: Array as PropType<ItemEntry[]>,
		required: true
	},
	options: {
		type: Array as PropType<IMultiselectOption[]>,
		required: true
	},
	modelValue: {
		type: [String, Number, null] as PropType<string | number | null>,
		required: true
	},
	placeholder: {
		type: String,
		default: 'Search'
	},
	groupName: {
		type: String,
		default: 'radio-select'
	},
	showLimit: {
		type: Number,
		default: 7
	},
	description: {
		type: String,
		default: ''
	},
	warning: {
		type: String,
		default: ''
	},
	highlightSelectedText: {
		type: Boolean,
		default: true
	}
});

const showAll = ref(false);

const showMoreIndicator = computed(() => {
	return props.items.length > props.showLimit;
});


const displayedItems = computed<ItemEntry[]>(() => {
	if (showAll.value) return props.items;

	const baseItems = props.items.slice(0, props.showLimit);
	const selectedValue = props.modelValue;
	if (selectedValue === null || selectedValue === undefined) return baseItems;

	const selectedEntry = props.items.find((entry) => {
		if (entry.item.value === selectedValue) return true;
		return entry.subItems?.some((subItem) => subItem.value === selectedValue) ?? false;
	});

	if (!selectedEntry) return baseItems;
	if (baseItems.some((entry) => entry.item.value === selectedEntry.item.value)) return baseItems;

	return props.items.filter((entry, index) =>
		index < props.showLimit || entry.item.value === selectedEntry.item.value
	);
});

const showAllText = computed(() => {
	return showAll.value ? `Show less` : `Show all (${props.items.length})`;
});

const showHideAll = () => {
	showAll.value = !showAll.value;
};
const emit = defineEmits<{
	(e: 'update:modelValue', value: string | number | null): void;
	(e: 'select', value: string | number, selected: boolean): void;
}>();

const multiselectValue = ref<string | number | undefined>(props.modelValue ?? undefined);

const multiselectProps = computed(() => ({
	options: props.options,
	multiselectValue: multiselectValue.value,
	mode: MultiselectMode.SINGLE,
	showOptionsOnSearchOnly: true,
	placeholder: props.placeholder,
	alwaysShowPlaceholder: true,
	description: props.description,
	warning: props.warning
}));

const handleItemChange = (item: ISelectItem, state: string): void => {
	const selected = state === SelectListItemStateType.SELECTED;

	if (selected) {
		multiselectValue.value = item.value;
		emit('select', item.value, true);
	} else if (multiselectValue.value === item.value) {
		multiselectValue.value = undefined;
		emit('select', item.value, false);
		emit('update:modelValue', null);
	}
};
const handleMultiselectUpdate = (value: (string | number)[] | string | number | null): void => {
	const normalized = Array.isArray(value) ? (value[0] ?? null) : value ?? null;
	if (!normalized) return;
	multiselectValue.value = normalized;
	emit('select', normalized as string | number, true);
	emit('update:modelValue', normalized);
};
</script>

<style scoped lang="scss">
.SearchableRadioFilter {
	display: flex;
	flex-direction: column;
	gap: 1rem;
	@include AddBorderBeforeElement(1, 2);	// required for outline not to be cut off

	.List {
		display: flex;
		flex-direction: column;
		overflow: auto;
		scrollbar-width: none;
		padding: 1rem 2px 0 0;	// required for outline not to be cut off
		border-top: 1px solid $Grey-200;

		&::-webkit-scrollbar {
			display: none;
		}
	}
	.ShowAll {
		display: flex;
		cursor: pointer;
		color: $Blue-700;
		background: none;
		border: none;
		@include PaddingAreas(0.5, 0);

		&:focus-visible {
			outline: none;
		}
	}
}
</style>
